import { useCallback, useState } from "react";
import { evaluate } from "mathjs";
import { useLaborFormulas } from "../../../hooks/query";

interface PrefilledValues {
    [laborServiceId: string]: {
        value?: string;
        quantity?: number;
    };
}

interface LaborService {
    Id: string;
    Name: string;
    CalculationFormula?: string | null;
}

const useLaborFormulaEvaluator = () => {
    const [computedValues, setComputedValues] = useState<Record<string, number | null>>({});
    const { useList } = useLaborFormulas();
    const { data: laborFormulas, isLoading: isLoadingLaborFormulas } = useList();

    const evaluateFormula = useCallback(
        (
            measurements: any[],
            laborServices: LaborService[],
            _prefilledValues: PrefilledValues,
            attributes?: any[],
        ): Record<string, number | null> => {
            const results: Record<string, number | null> = {};
            const measurementsObject = convertMeasurementIntoKeyValuePair(measurements);
            const attributesScope = convertAttributesIntoScope(attributes);
            const scope = { ...measurementsObject, ...attributesScope };

            laborServices.forEach((service) => {
                // First try: use the formula stored on the service record (from DB)
                // Second try: fall back to the formula from the backend labor-formulas lookup by service Name
                const expression =
                    service.CalculationFormula ||
                    (laborFormulas && !isLoadingLaborFormulas
                        ? laborFormulas[service.Name]?.expression
                        : null);

                if (expression) {
                    try {
                        let result = evaluate(expression, scope) as number;

                        // Apply output format — prefer backend definition, default to WholeNumber
                        const outputFormat =
                            laborFormulas && laborFormulas[service.Name]?.outputFormat;
                        if (!outputFormat || outputFormat === "WholeNumber") {
                            result = Math.ceil(result);
                        }

                        results[service.Id] = result;
                    } catch (error) {
                        results[service.Id] = 0;
                    }
                } else {
                    results[service.Id] = 0;
                }
            });

            setComputedValues(results);
            return results;
        },
        [laborFormulas, isLoadingLaborFormulas],
    );

    const convertMeasurementIntoKeyValuePair = (measurements: any) => {
        if (!measurements) return getDefaultValues();

        const result = measurements.reduce((acc: any, measurement: any) => {
            const value = measurement.ActualValue ?? measurement.CalculatedValue;
            acc[measurement.MeasurementType.replace(/\s+/g, "")] = Number(value) || 0;
            // Also keep original name with spaces for compatibility
            acc[measurement.MeasurementType] = Number(value) || 0;
            return acc;
        }, {});

        return { ...getDefaultValues(), ...result };
    };

    const getDefaultValues = () => ({
        Area: 0,
        Eave: 0,
        Rake: 0,
        Ridge: 0,
        Hip: 0,
        Valley: 0,
        Headwall: 0,
        Step: 0,
        RollMetal: 0,
        CounterFlashing: 0,
        RoofVent: 0,
        RVs: 0,
        Skylight: 0,
        Skylights: 0,
        Chimney: 0,
        PipeJack: 0,
        Pjack: 0,
        SplitBoots: 0,
        SplitsBoots: 0,
        FurnaceVents: 0,
        Furn: 0,
        BathroomVents: 0,
        BathVents: 0,
        DryerVents: 0,
        GutterLength: 0,
        DownspoutLength: 0,
        SolarPanels: 0,
        Cricket: 0,
        Cooler: 0,
    });

    const convertAttributesIntoScope = (attributes?: any[]): Record<string, any> => {
        if (!attributes || attributes.length === 0) return {};
        const scope: Record<string, any> = {};
        attributes.forEach((attr) => {
            const rawName: string = attr.AttributeName;
            if (!rawName) return;
            const camelKey = rawName.replace(/\s+(\w)/g, (_: string, c: string) => c.toUpperCase());
            const raw = attr.AttributeValue;
            if (raw === null || raw === undefined || raw === "") return;
            let parsed: any;
            try { parsed = JSON.parse(raw); } catch { parsed = null; }
            let value: any;
            if (Array.isArray(parsed)) {
                const total = parsed.reduce((sum: number, item: any) => sum + (Number(item.count) || 0), 0);
                value = total > 0 ? total : parsed.length;
            } else {
                const num = Number(raw);
                value = (!isNaN(num) && raw !== "") ? num : raw;
            }
            scope[rawName] = value;
            scope[camelKey] = value;
        });
        return scope;
    };

    return { computedValues, evaluateFormula, isLoadingLaborFormulas };

};

export default useLaborFormulaEvaluator;
